import { observable, autorun, makeObservable } from 'mobx'
import * as R from 'ramda'
import i18n from 'i18next'
import { sortBy } from '../../utils/helpers'

class PortfolioStore {
    @observable filteredList = []

    industries = []

    industry = null

    divisions = []

    division = null

    isMultiDivision = false

    products = []

    @observable name = ''

    @observable availableTags = []

    @observable recordTags = []

    @observable formFields = []

    @observable formLoading = true

    constructor(store) {
        makeObservable(this)
        this.appStore = store
        autorun(() => {
            if (this.formFields.length > 0) this.formLoading = false
        })
    }

    initSearch = async () => {
        [this.portfolios, this.industries] = await Promise.all(
            [this.getPortfoliosData(), this.getIndustryData()],
        )

        const industryMap = this.industries.reduce((map, industry) => {
            map[industry.code] = industry.name
            return map
        }, {})

        this.portfolios = this.portfolios.map((p) => {
            p.industry = industryMap[p.industry] || p.industry
            return p
        })

        this.filteredList = this.portfolios
    }

    // for PortfolioEdit.jsx
    initPortfolio = async (portfolioId, handleCloseModal) => {
        this.formLoading = true

        try {
            this.divisions = await this.getDivisionData()

            const industries = await this.getIndustryData()
            this.industries = industries.map(({ code, name }) => ({
                value: code,
                label: name,
            }))
            const hasMultiDivisions = R.pathOr(
                false,
                ['userData', 'flags', 'isMultiDivision'],
                this.appStore,
            )
            // for portfolio edit
            if (portfolioId !== 0) {
                const portfolio = await this.getPortfolioData(portfolioId)
                this.name = portfolio.name
                this.products = sortBy(portfolio.product, 'name').map((p) => ({
                    productId: p.productId,
                    name: p.name,
                    selected: false,
                }))
                this.users = portfolio.user

                const foundDivision = this.divisions.find((d) => d.value === portfolio.divisionId)
                if (foundDivision) this.division = foundDivision.value
                const foundIndustry = this.industries.find((i) => i.value === portfolio.industry)
                if (foundIndustry) this.industry = foundIndustry.value
            } else if (hasMultiDivisions) {
                this.isMultiDivision = true
            } else {
                this.division = this.divisions[0].value
            }

            this.defineFormFields(portfolioId, this.industries, this.divisions, this.users)

            await this.getTags(portfolioId)
        } catch (error) {
            console.log('portfolio store error ', error)
            handleCloseModal()
            console.error('PortfolioStore.jsx initPortfolio error ', error)
        }
    }

    defineFormFields = (portfolioId, industries, divisions) => {
        this.formFields = [
            {
                type: 'input',
                name: 'name',
                label: i18n.t('portfolio.form.name'),
                placeholder: 'Portfolio name',
                defaultValue: this.name,
                isMandatory: true,
                errorMessage: 'Name required',
            },
            {
                type: 'select',
                name: 'industry',
                label: i18n.t('portfolio.form.industry'),
                defaultValue: this.industry,
                options: industries,
                isMandatory: true,
                errorMessage: 'Industry required',
                isDisabled: portfolioId !== 0,
            },
            {
                type: 'select',
                name: 'division',
                label: i18n.t('portfolio.form.division'),
                defaultValue: this.division,
                options: divisions,
                isMandatory: true,
                errorMessage: 'Division required',
                isDisabled: portfolioId !== 0,
                isHide: !R.pathOr(false, ['userData', 'flags', 'isMultiDivision'], this.appStore),
            },
        ]
    }

    setField = async ({ value, name }) => {
        console.log(`setField ${value} => ${name}`)
        this[name] = value

        if (['industry', 'division'].includes(name)) {
            await this.getTags(this.portfolioId)
        }
    }

    resetFormState = () => {
        this.formFields = []
        this.industry = null
        this.division = null
        this.name = ''
    }

    getDivisionData = async () => {
        const divisions = await this.appStore.http.get('/division')
        return divisions.map(({ divisionId, name }) => ({
            value: divisionId,
            label: name,
        }))
    }

    getPortfolioData = (portfolioId) => (
        this.appStore.http.get(`/portfolio/${portfolioId}`)
    )

    getPortfoliosData = () => (this.appStore.http.get('/portfolio'))

    getIndustryData = () => (this.appStore.http.get('/client/industry'))

    search = () => {
        const { searchStr } = this.searchModule
        if (searchStr === '') {
            this.filteredList = this.portfolios
        } else if (!Number.isNaN(parseInt(searchStr, 10))) {
            this.filteredList = this.portfolios
                .filter((p) => p.portfolioId === parseInt(searchStr, 10))
        } else {
            const re = new RegExp(searchStr, 'i')
            this.filteredList = this.portfolios
                .filter((p) => p.name.match(re))
        }
    }

    update = async (obj) => {
        const response = await this.appStore.http.put(
            `/portfolio/${obj.portfolioId}`,
            JSON.stringify({
                ...obj,
                user: this.users,
                product: this.products.map((p) => ({ name: p.name, productId: p.productId })),
            }),
        )
        return response
    }

    save = async (obj) => {
        const response = await this.appStore.http.post('/portfolio', JSON.stringify(obj))
        return response
    }

    /** Tags */
    getAvailableTags = async (params) => {
        const { appStore } = this

        return appStore.getAvailableTags(params)
    }

    getTags = async (portfolioId) => {
        const { appStore } = this

        this.availableTags = await appStore.getAvailableTags({
            oid: 5,
            portfolioId,
            industry: this.industry,
            divisionId: this.division,
        })
        let updatedRecordTags = {}
        if (portfolioId !== 0) updatedRecordTags = await this.appStore.getRecordTags(5, portfolioId)
        this.recordTags = updatedRecordTags
    }

    setTag = ({ name, value }) => {
        console.log('setTag', name, value)
        this.recordTags[name] = value
    }

    saveTags = async (recordId) => {
        const { appStore } = this
        try {
            appStore.saveTags(recordId, 5, this.availableTags, this.recordTags)
        } catch (e) {
            console.error(e)
        }
    }
    /** end Tags */
}

export default PortfolioStore
